import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { map, retry, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** one priced model on one platform, USD per million tokens, as GET /ai/prices serves it */
export interface AiModelPrice {
  id: string;
  name: string;
  vendor: string;
  platform: string;
  family: string;
  input: number;
  output: number;
  cacheRead: number | null;
  cacheWrite: number | null;
  context: number | null;
  maxOutput: number | null;
  reasoning: boolean;
  vision: boolean;
  deprecation: string | null;
  source: string | null;
}

export interface AiPlatform {
  label: string;
  operator: string;
}

export interface AiPrices {
  ok: boolean;
  syncedAt: string | null;
  platforms: { [ key: string ]: AiPlatform };
  models: AiModelPrice[];
}

/**
 * The AI model prices (2026-10-19): the server reads LiteLLM's public table once a day and serves
 * `GET /ai/prices`; the site never carries a price of its own. One request per visit, shared by every
 * page that shows prices; while the server cannot be reached the request is repeated every 30 seconds.
 */
@Injectable( { providedIn: 'root' } )
export class AiPricesService {

  readonly prices$: Observable<AiPrices> = this.http.get<AiPrices>( `${ environment.serverUrl }/ai/prices` ).pipe(
    map( data => {
      if ( !data || !data.ok || !Array.isArray( data.models ) ) { throw new Error( 'no prices' ); }
      return data;
    } ),
    retry( { delay: () => timer( 30 * 1000 ) } ),
    shareReplay( { bufferSize: 1, refCount: false } ),
  );

  constructor( private http: HttpClient ) {
  }

  /** "$3.00", "$0.035", "Free": two decimals, more when a small price needs them */
  static price( value: number | null | undefined ): string {
    if ( value === null || value === undefined || !isFinite( value ) ) { return ''; }
    if ( value === 0 ) { return 'Free'; }
    const decimals = value >= 0.1 ? 2 : value >= 0.01 ? 3 : 4;
    let text = value.toFixed( decimals );
    if ( decimals > 2 ) { text = text.replace( /0+$/, '' ).replace( /\.$/, '' ); }
    return '$' + text;
  }

  /** "200K", "1M", "1.05M" */
  static tokens( value: number | null | undefined ): string {
    if ( !value ) { return ''; }
    if ( value >= 1000000 ) { return ( value / 1000000 ).toFixed( 2 ).replace( /\.?0+$/, '' ) + 'M'; }
    if ( value >= 1000 ) { return Math.round( value / 1000 ) + 'K'; }
    return String( value );
  }

  /** "Oct 19, 2026" from the sync time */
  static day( iso: string | null ): string {
    if ( !iso ) { return ''; }
    const d = new Date( iso );
    return isNaN( d.getTime() ) ? '' : d.toLocaleDateString( 'en-US', { month: 'short', day: 'numeric', year: 'numeric' } );
  }
}
